/**
 * Zeitraster-Dialog (Zahnrad im Stundenplan): kompakte Liste mit Beginn und
 * Ende je Unterrichtsstunde. Die Pausen dazwischen werden nicht eingegeben,
 * sondern laufend aus den Zeiten berechnet und als schlichte Trennlinie
 * gezeigt.
 */

import { h, clear } from "@/lib/dom";
import { button, openModal, toast } from "@/components/ui";
import { pbErrorMessage, timetableService } from "@/services";
import {
  DEFAULT_PERIODS,
  breakAfter,
  fromMinutes,
  normalizePeriods,
  overlappingPeriods,
  toMinutes,
} from "./schedule";
import type { Period, Weekday } from "@/types/models";

/** Nach welcher Stunde eine neue standardmäßig beginnt (übliche kurze Pause). */
const DEFAULT_GAP = 5;
const DEFAULT_LENGTH = 45;

export function openPeriodsModal(opts: {
  periods: Period[];
  days: Weekday[];
  onSaved: () => void;
}): void {
  // Auf einer Kopie arbeiten – der Plan ändert sich erst beim Speichern.
  let rows: Period[] = opts.periods.map((p) => ({ ...p }));
  // Angezeigte Wochentage sind vorerst nicht editierbar (siehe unten) – der
  // bestehende Wert wird unverändert mitgespeichert.
  const days: Weekday[] = [...opts.days];

  const table = h("div", { class: "period-table" });
  const hint = h("div", { class: "field-hint is-warn" });

  function nextRow(): Period {
    const last = rows[rows.length - 1];
    const lastEnd = last ? toMinutes(last.end) : null;
    const start = lastEnd === null ? 8 * 60 : lastEnd + DEFAULT_GAP;
    return { start: fromMinutes(start), end: fromMinutes(start + DEFAULT_LENGTH) };
  }

  function timeInput(value: string, onChange: (v: string) => void): HTMLInputElement {
    const input = h("input", {
      class: "input time-input",
      type: "time",
      value,
      step: 300,
    });
    // `change` statt `input`: sonst rendert die Tabelle bei jedem Tastendruck
    // neu und der Fokus springt aus dem Feld.
    input.addEventListener("change", () => onChange(input.value));
    return input;
  }

  function render(): void {
    clear(table);

    rows.forEach((row, i) => {
      table.appendChild(
        h(
          "div",
          { class: "period-row" },
          h("span", { class: "p-num" }, String(i + 1)),
          timeInput(row.start, (v) => {
            rows[i].start = v;
            render();
          }),
          h("span", { class: "p-dash" }, "–"),
          timeInput(row.end, (v) => {
            rows[i].end = v;
            render();
          }),
          button({
            icon: "trash",
            variant: "ghost",
            size: "sm",
            title: `${i + 1}. Stunde entfernen`,
            onClick: () => {
              rows.splice(i, 1);
              render();
            },
          })
        )
      );

      // Pause zur nächsten Stunde – abgeleitet, nicht eingegeben. Nur eine
      // ruhige graue Linie, keine Minutenangabe; nahtlose Doppelstunden
      // bekommen gar keine Linie.
      if (i < rows.length - 1 && breakAfter(rows, i) > 0) {
        table.appendChild(h("div", { class: "period-break" }));
      }
    });

    const clean = normalizePeriods(rows);
    const overlaps = overlappingPeriods(rows);
    const dropped = rows.length - clean.length;
    const problems: string[] = [];
    if (overlaps.length) {
      problems.push(`Stunde ${overlaps.join(", ")} überschneidet sich mit der vorherigen.`);
    }
    if (dropped > 0) {
      problems.push(`${dropped} Zeile(n) ohne gültige Zeiten werden verworfen.`);
    }
    hint.textContent = problems.join(" ");
    hint.classList.toggle("is-hidden", problems.length === 0);
  }

  const body = h(
    "div",
    { class: "periods-modal" },
    table,
    hint,
    h(
      "div",
      { class: "row periods-actions" },
      button({
        label: "Stunde hinzufügen",
        icon: "plus",
        size: "sm",
        variant: "ghost",
        onClick: () => {
          rows.push(nextRow());
          render();
        },
      }),
      button({
        label: "Standard",
        icon: "undo",
        variant: "ghost",
        size: "sm",
        onClick: () => {
          rows = DEFAULT_PERIODS.map((p) => ({ ...p }));
          render();
        },
      })
    )
  );

  const saveBtn = button({
    label: "Speichern",
    variant: "primary",
    onClick: () => void save(),
  });

  async function save(): Promise<void> {
    const clean = normalizePeriods(rows);
    if (!clean.length) {
      toast("Mindestens eine gültige Stunde angeben.", "warning");
      return;
    }
    if (!days.length) {
      toast("Mindestens einen Wochentag auswählen.", "warning");
      return;
    }
    saveBtn.disabled = true;
    try {
      await timetableService.saveSettings({ periods: clean, days });
      handle.close();
      toast("Zeitraster gespeichert.", "success");
      opts.onSaved();
    } catch (err) {
      saveBtn.disabled = false;
      toast(pbErrorMessage(err), "error");
    }
  }

  render();

  const handle = openModal({
    title: "Zeitraster",
    body,
    footer: h(
      "div",
      { class: "row" },
      button({ label: "Abbrechen", variant: "ghost", onClick: () => handle.close() }),
      saveBtn
    ),
  });
}
